import { useEffect, useRef, useState } from "react";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { toast } from "sonner";
import { Mic, Square, Upload, Trash2, Plus, FileText, ImageIcon, MessageSquare, Tag } from "lucide-react";
import { useBilling } from "../context/BillingContext";

const TYPES = [
  { key: "text", label: "Note", icon: MessageSquare },
  { key: "voice", label: "Voice", icon: Mic },
  { key: "photo", label: "Photo", icon: ImageIcon },
  { key: "doc", label: "Document", icon: FileText },
  { key: "story", label: "Team/Client story", icon: Tag },
];

export default function Musings() {
  const { handleApiError } = useBilling();
  const [items, setItems] = useState([]);
  const [type, setType] = useState("text");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/musings");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const startRecord = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "musing.webm");
          const { data } = await api.post("/musings/transcribe", fd);
          setContent((c) => c ? c + "\n\n" + data.text : data.text);
          toast.success("Transcribed");
        } catch (e) {
          if (!handleApiError(e)) toast.error("Transcription failed");
        } finally { setBusy(false); }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) { toast.error("Mic access denied"); }
  };
  const stopRecord = () => { mediaRef.current?.stop(); setRecording(false); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      setUploadedFile(data);
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/musings", {
        musing_type: type,
        title: title.trim(),
        content: content.trim(),
        tags: tags.split(",").map(s=>s.trim()).filter(Boolean),
        file_ids: uploadedFile ? [uploadedFile.id] : [],
      });
      setTitle(""); setContent(""); setTags(""); setUploadedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success("Musing captured");
      load();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/musings/${id}`);
    load();
  };

  return (
    <AppShell eyebrow="Raw material" title="Musings">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200">
        <form onSubmit={submit} className="bg-white p-6 lg:col-span-2 space-y-4">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-[#0033FF]">New musing</div>
          <div className="flex gap-1 flex-wrap">
            {TYPES.map((t) => (
              <button key={t.key} type="button" data-testid={`musing-type-${t.key}`}
                onClick={()=>setType(t.key)}
                className={`px-3 py-2 text-xs font-bold tracking-wider uppercase border ${type===t.key ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:border-zinc-900"}`}>
                <t.icon className="w-3 h-3 inline mr-1 -mt-0.5" />{t.label}
              </button>
            ))}
          </div>

          <input data-testid="musing-title-input" className="w-full px-3 py-2.5 border border-zinc-300 focus:outline-none focus:border-[#0033FF] text-sm" placeholder="Title (optional)" value={title} onChange={(e)=>setTitle(e.target.value)} />

          <textarea data-testid="musing-content-input" className="w-full px-3 py-2.5 border border-zinc-300 focus:outline-none focus:border-[#0033FF] text-sm min-h-[160px] leading-relaxed"
            placeholder={type === "voice" ? "Speak; transcript will appear here. Or type." : "What's on your mind?"} value={content} onChange={(e)=>setContent(e.target.value)} />

          {type === "voice" && (
            <div>
              {!recording ? (
                <button type="button" data-testid="musing-record-start" onClick={startRecord} disabled={busy}
                  className="px-4 py-2 border border-zinc-900 text-xs font-bold tracking-wider uppercase hover:bg-zinc-900 hover:text-white inline-flex items-center gap-2">
                  <Mic className="w-3.5 h-3.5" /> Start recording
                </button>
              ) : (
                <button type="button" data-testid="musing-record-stop" onClick={stopRecord}
                  className="px-4 py-2 bg-[#FF3B30] text-white text-xs font-bold tracking-wider uppercase inline-flex items-center gap-2">
                  <Square className="w-3.5 h-3.5" /> Stop
                </button>
              )}
            </div>
          )}

          {(type === "photo" || type === "doc") && (
            <div>
              <input ref={fileInputRef} data-testid="musing-file-input" type="file" onChange={onFile}
                accept={type === "photo" ? "image/*" : ".pdf,.doc,.docx,.txt,.md"} className="hidden"/>
              <button type="button" data-testid="musing-upload-btn" onClick={()=>fileInputRef.current?.click()} disabled={busy}
                className="px-4 py-2 border border-zinc-900 text-xs font-bold tracking-wider uppercase hover:bg-zinc-900 hover:text-white inline-flex items-center gap-2">
                <Upload className="w-3.5 h-3.5" /> {uploadedFile ? "Replace file" : "Upload file"}
              </button>
              {uploadedFile && <div className="text-xs text-zinc-600 mt-2">Attached: {uploadedFile.original_filename}</div>}
            </div>
          )}

          <input data-testid="musing-tags-input" className="w-full px-3 py-2.5 border border-zinc-300 focus:outline-none focus:border-[#0033FF] text-sm" placeholder="Tags (comma separated)" value={tags} onChange={(e)=>setTags(e.target.value)} />

          <button type="submit" data-testid="musing-submit-btn" disabled={busy || !content.trim()}
            className="w-full bg-[#0033FF] text-white px-4 py-3 text-xs font-bold tracking-wider uppercase hover:bg-zinc-900 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            <Plus className="w-3.5 h-3.5" /> Capture
          </button>
        </form>

        <div className="bg-white p-6 lg:col-span-3">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-[#0033FF] mb-4">Your stream</div>
          <div className="divide-y divide-zinc-200 border-t border-zinc-200">
            {items.length === 0 && <div className="py-8 text-sm text-zinc-500" data-testid="musings-empty">No musings yet. Drop your first thought →</div>}
            {items.map((m) => (
              <div key={m.id} className="py-4 group" data-testid={`musing-item-${m.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0033FF]">{m.musing_type}</span>
                      <span className="text-xs text-zinc-500 font-mono">{new Date(m.created_at).toLocaleString()}</span>
                    </div>
                    {m.title && <div className="text-base font-bold tracking-tight">{m.title}</div>}
                    <div className="text-sm text-zinc-700 whitespace-pre-wrap mt-1">{m.content}</div>
                    {m.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {m.tags.map((t,i)=>(<span key={i} className="text-[10px] tracking-wider uppercase px-2 py-0.5 bg-zinc-100">{t}</span>))}
                      </div>
                    )}
                  </div>
                  <button data-testid={`musing-delete-${m.id}`} onClick={()=>remove(m.id)} className="text-zinc-400 hover:text-[#FF3B30] opacity-0 group-hover:opacity-100">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
